import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as telegramService from '@services/telegram.service.ts';
import { TelegramStatus } from '@services/telegram.service.ts';
import { toastService } from '@services/toast.service.ts';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';

export const TelegramSettingsTab: React.FC = () => {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await telegramService.getTelegramStatus();
      setStatus(data.status);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch telegram status:', err);
      setError('Не вдалося завантажити статус підключення Telegram.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleConnect = async () => {
    try {
      setLinkLoading(true);
      const data = await telegramService.getTelegramLink();
      const link = data?.link;

      if (!link || link === 'https://t.me/' || link.includes('undefined')) {
        console.error('Invalid telegram link received:', link);
        toastService.error('Бот не налаштований належним чином на сервері (відсутній username).');
        return;
      }

      const win = window.open(link, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        window.location.href = link;
      }
      
      setTimeout(() => fetchStatus(), 3000);
    } catch (err) {
      console.error('Failed to get telegram link:', err);
      toastService.error('Не вдалося отримати посилання для підключення.');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnectLoading(true);
      await telegramService.disconnectTelegram();
      toastService.success('Підключення Telegram успішно анульовано.');
      setIsDisconnectDialogOpen(false);
      await fetchStatus();
    } catch (err) {
      console.error('Failed to disconnect telegram:', err);
      toastService.error('Не вдалося анулювати підключення.');
    } finally {
      setDisconnectLoading(false);
    }
  };

  const getStatusDisplay = () => {
    switch (status) {
      case 'active':
        return {
          label: 'Підключено',
          color: 'success' as const,
          icon: <CheckCircleIcon />,
          description: 'Ваш Telegram аккаунт підключено. Ви будете отримувати сповіщення від бота.',
        };
      case 'pending':
        return {
          label: 'Очікує підключення',
          color: 'warning' as const,
          icon: <PendingIcon />,
          description: 'Ви ініціювали підключення. Перейдіть до бота та натисніть /start для завершення.',
        };
      case 'blocked':
        return {
          label: 'Заблоковано',
          color: 'error' as const,
          icon: <BlockIcon />,
          description: 'З\'єднання з Telegram заблоковано сервером.',
        };
      case 'NOT_CONNECTED':
      default:
        return {
          label: 'Не підключено',
          color: 'default' as const,
          icon: <ErrorOutlineIcon />,
          description: 'Ви ще не підключили Telegram бот для отримання сповіщень.',
        };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const statusInfo = getStatusDisplay();

  return (
    <Box data-i-doc={TabIndices.SETTINGS_TELEGRAM}>
      <Typography variant="h6" gutterBottom>
        Telegram сповіщення
      </Typography>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TelegramIcon sx={{ fontSize: 40, color: '#26A5E4' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Статус підключення
              </Typography>
              <Chip
                icon={statusInfo.icon}
                label={statusInfo.label}
                color={statusInfo.color}
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          <Alert severity={statusInfo.color === 'default' ? 'info' : statusInfo.color}>
            <AlertTitle>Про Telegram бот</AlertTitle>
            {statusInfo.description}
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {(status === 'NOT_CONNECTED' || status === 'pending') && (
              <Button
                variant="contained"
                startIcon={<TelegramIcon />}
                onClick={handleConnect}
                disabled={linkLoading}
              >
                {linkLoading ? <CircularProgress size={24} /> : 'Підключити Telegram'}
              </Button>
            )}

            {status !== 'NOT_CONNECTED' && (
              <>
                <Button
                  variant="outlined"
                  onClick={fetchStatus}
                  disabled={loading}
                >
                  Оновити статус
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setIsDisconnectDialogOpen(true)}
                  disabled={disconnectLoading}
                >
                  Анулювати підключення
                </Button>
              </>
            )}
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={isDisconnectDialogOpen}
        onClose={() => setIsDisconnectDialogOpen(false)}
      >
        <DialogTitle>Анулювати підключення Telegram?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви припините отримувати сповіщення в Telegram від нашого бота. 
            Ви зможете підключити його знову у будь-який час.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDisconnectDialogOpen(false)}>Скасувати</Button>
          <Button 
            onClick={handleDisconnect} 
            color="error" 
            autoFocus
            disabled={disconnectLoading}
          >
            {disconnectLoading ? <CircularProgress size={24} /> : 'Так, анулювати'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
